import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from "../config/fbConfig";
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  setDoc,
  Timestamp,
} from "firebase/firestore";
import Modal from "react-modal";
import Logo from "../images/school_logo.png";
import Loader from "./Loader";
import SuccessModal from "./SuccessModal";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {SCHOOL_NAME, SCHOOL_ADDRESS, SCHOOL_PHONE } from '../Variables.js';

Modal.setAppElement("#root");

const Timetable = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [update, setUpdate] = useState(false);
  const [allTeachers, setAllTeachers] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teacherAssignments, setTeacherAssignments] = useState({});
  const [timetables, setTimetables] = useState({});
  const [isSelectTeachersModalOpen, setIsSelectTeachersModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isSetPeriodsModalOpen, setIsSetPeriodsModalOpen] = useState(false);
  const [isEditCellModalOpen, setIsEditCellModalOpen] = useState(false);
  const [isEditAssignmentModalOpen, setIsEditAssignmentModalOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedPeriods, setSelectedPeriods] = useState(1);
  const [editCell, setEditCell] = useState(null);
  const [editAssignment, setEditAssignment] = useState(null);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [periodsPerDay, setPeriodsPerDay] = useState({
    Monday: 3,
    Tuesday: 3,
    Wednesday: 3,
    Thursday: 3,
    Friday: 3,
  });
  
  // When initializing periodConfig, ensure it has enough elements:
  const [periodConfig, setPeriodConfig] = useState(
    Array(12).fill().map((_, i) => ({
      time: `8:${i*40 % 60 === 0 ? '00' : '40'} - 8:${(i+1)*40 % 60 === 0 ? '00' : '40'}`,
      isBreak: false
    }))
  );

  const teachersCollectionRef = collection(db, "users");
  const classesCollectionRef = collection(db, "classes");
  const subjectsCollectionRef = collection(db, "subjects");
  const timetablesCollectionRef = collection(db, "timetables");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsFetchingData(true);
    try {
      const [teachersSnap, classesSnap, subjectsSnap, timetablesSnap] = await Promise.all([
        getDocs(teachersCollectionRef),
        getDocs(classesCollectionRef),
        getDocs(subjectsCollectionRef),
        getDocs(timetablesCollectionRef),
      ]);
      
      setAllTeachers(
        teachersSnap.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .filter((user) => user.status === "Teacher")
      );
      
      const uniqueClasses = [
        ...new Set(
          classesSnap.docs.map((doc) => ({ ...doc.data(), id: doc.id })).map((c) => c.name)
        ),
      ].map((name) => ({
        name,
        id: classesSnap.docs.find((doc) => doc.data().name === name)?.id,
      }));
      setClasses(uniqueClasses);
      setSubjects(subjectsSnap.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
  
      const savedTimetables = {};
      const timetablesByClass = {};
      timetablesSnap.docs.forEach((doc) => {
        const classId = doc.id;
        const createdAt = doc.data().createdAt?.toDate() || new Date(0);
        
        if (!timetablesByClass[classId] || 
            (timetablesByClass[classId].createdAt < createdAt)) {
          timetablesByClass[classId] = {
            timetable: doc.data().timetable,
            createdAt: createdAt,
            periodsPerDay: doc.data().periodsPerDay || periodsPerDay,
            periodConfig: doc.data().periodConfig || periodConfig
          };
        }
      });
       
      Object.keys(timetablesByClass).forEach(classId => {
        const timetable = timetablesByClass[classId].timetable;
        const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
        days.forEach(day => {
          if (!timetable[day] || !Array.isArray(timetable[day])) {
            console.warn(`Invalid timetable data for classId: ${classId}, day: ${day}`);
            timetable[day] = Array(periodsPerDay[day]).fill('-');
          }
        });
        savedTimetables[classId] = timetable;
        setPeriodsPerDay(timetablesByClass[classId].periodsPerDay);
        setPeriodConfig(timetablesByClass[classId].periodConfig);
      });
  
      setTimetables(savedTimetables);
    } catch (error) {
      setError("Failed to fetch data");
      setShowModal(true);
    } finally {
      setIsFetchingData(false);
    }
  };

  const handleSelectTeachers = () => {
    setIsSelectTeachersModalOpen(true);
  };

  const confirmSelectedTeachers = () => {
    setIsSelectTeachersModalOpen(false);
  };

  const handleAssignSubject = () => {
    setError("");
    setSuccessMessage("");
    if (!selectedTeacher || !selectedSubject || !selectedClass || !selectedPeriods) {
      setError("Please select a teacher, subject, class, and number of periods");
      setShowModal(true);
      return;
    }

    setTeacherAssignments((prev) => {
      const updated = { ...prev };
      if (!updated[selectedTeacher]) updated[selectedTeacher] = [];
      if (
        !updated[selectedTeacher].some(
          (assignment) =>
            assignment.subjectId === selectedSubject &&
            assignment.classId === selectedClass
        )
      ) {
        updated[selectedTeacher].push({
          subjectId: selectedSubject,
          classId: selectedClass,
          periods: selectedPeriods,
        });
      }
      return updated;
    });
    setIsAssignModalOpen(false);
    setSelectedSubject("");
    setSelectedClass("");
    setSelectedPeriods(1);
  };

  const handleEditAssignment = (teacherId, index) => {
    const assignment = teacherAssignments[teacherId][index];
    setEditAssignment({ teacherId, index });
    setSelectedTeacher(teacherId);
    setSelectedSubject(assignment.subjectId);
    setSelectedClass(assignment.classId);
    setSelectedPeriods(assignment.periods);
    setIsEditAssignmentModalOpen(true);
  };

  const saveAssignmentEdit = () => {
    if (!selectedTeacher || !selectedSubject || !selectedClass || !selectedPeriods || !editAssignment) {
      setError("Please select a subject, class, and number of periods");
      setShowModal(true);
      return;
    }

    setTeacherAssignments((prev) => {
      const updated = { ...prev };
      updated[editAssignment.teacherId][editAssignment.index] = {
        subjectId: selectedSubject,
        classId: selectedClass,
        periods: selectedPeriods,
      };
      return updated;
    });

    setIsEditAssignmentModalOpen(false);
    setSelectedTeacher("");
    setSelectedSubject("");
    setSelectedClass("");
    setSelectedPeriods(1);
    setEditAssignment(null);
  };

  const removeAssignment = (teacherId, index) => {
    setTeacherAssignments((prev) => {
      const updated = { ...prev };
      updated[teacherId] = updated[teacherId].filter((_, i) => i !== index);
      if (updated[teacherId].length === 0) delete updated[teacherId];
      return updated;
    });
  };

  const setPeriodsForDay = (day, periods) => {
    setPeriodsPerDay((prev) => ({
      ...prev,
      [day]: Math.min(Math.max(3, periods), 12),
    }));
  };

  const handleEditCell = (classId, day, period) => {
    if (periodConfig[period].isBreak) return;
    setError("");
    setSuccessMessage("");
    setEditCell({ classId, day, period });
    setSelectedTeacher("");
    setSelectedSubject("");
    setIsEditCellModalOpen(true);
  };

  const saveCellEdit = () => {
    setError("");
    setSuccessMessage("");
    if (!selectedTeacher || !selectedSubject || !editCell) {
      setError("Please select a teacher and subject");
      setShowModal(true);
      return;
    }

    setTimetables((prev) => {
      const updated = { ...prev };
      updated[editCell.classId][editCell.day][editCell.period] = {
        teacherId: selectedTeacher,
        subjectId: selectedSubject,
      };
      return updated;
    });

    saveTimetables(timetables);
    setIsEditCellModalOpen(false);
    setSelectedTeacher("");
    setSelectedSubject("");
    setEditCell(null);
  };

  const removeCellSubject = () => {
    setError("");
    setSuccessMessage("");
    if (!editCell) {
      setError("No cell selected");
      setShowModal(true);
      return;
    }

    setTimetables((prev) => {
      const updated = { ...prev };
      updated[editCell.classId][editCell.day][editCell.period] = "-";
      return updated;
    });

    saveTimetables(timetables);
    setIsEditCellModalOpen(false);
    setSelectedTeacher("");
    setSelectedSubject("");
    setEditCell(null);
  };

  const generateTimetables = () => {
    setError("");
    setSuccessMessage("");
    const newTimetables = {};
    const teacherSchedules = {};
    const classSubjectDays = {};
    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

    Object.keys(teacherAssignments).forEach((teacherId) => {
      if (!teacherSchedules[teacherId]) {
        teacherSchedules[teacherId] = new Set();
      }
      const assignments = teacherAssignments[teacherId];

      assignments.forEach((assignment) => {
        const classId = assignment.classId;
        if (!newTimetables[classId]) {
          newTimetables[classId] = {};
          days.forEach((day) => {
            newTimetables[classId][day] = Array(periodsPerDay[day]).fill(null);
          });
        }
        if (!classSubjectDays[classId]) classSubjectDays[classId] = {};
        days.forEach((day) => {
          if (!classSubjectDays[classId][day]) classSubjectDays[classId][day] = new Set();
        });
      });
    });

    const allSlots = [];
    Object.keys(newTimetables).forEach((classId) => {
      days.forEach((day) => {
        for (let period = 0; period < periodsPerDay[day]; period++) {
          if (!periodConfig[period].isBreak) {
            allSlots.push({ classId, day, period });
          }
        }
      });
    });

    for (let i = allSlots.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allSlots[i], allSlots[j]] = [allSlots[j], allSlots[i]];
    }

    Object.keys(teacherAssignments).forEach((teacherId) => {
      const assignments = teacherAssignments[teacherId];
      assignments.forEach((assignment) => {
        let periodsToPlace = assignment.periods;
        const classId = assignment.classId;
        const subjectId = assignment.subjectId;

        for (const slot of allSlots) {
          if (periodsToPlace <= 0) break;

          if (
            slot.classId === classId &&
            !newTimetables[classId][slot.day][slot.period] &&
            !teacherSchedules[teacherId].has(`${slot.day}-${slot.period}`) &&
            !classSubjectDays[classId][slot.day].has(subjectId)
          ) {
            newTimetables[classId][slot.day][slot.period] = {
              teacherId,
              subjectId,
            };
            teacherSchedules[teacherId].add(`${slot.day}-${slot.period}`);
            classSubjectDays[classId][slot.day].add(subjectId);
            periodsToPlace--;
          }
        }
      });
    });

    Object.keys(newTimetables).forEach((classId) => {
      days.forEach((day) => {
        newTimetables[classId][day] = newTimetables[classId][day].map((slot, index) =>
          slot && typeof slot === "object" ? slot : periodConfig[index].isBreak ? "Break" : "-"
        );
      });
    });

    setTimetables(newTimetables);
    saveTimetables(newTimetables);
  };

  const saveTimetables = async (timetableData) => {
    setError("");
    setSuccessMessage("");
    setUpdate(true);
    try {
      await Promise.all(
        Object.keys(timetableData).map((classId) =>
          setDoc(doc(timetablesCollectionRef, classId), {
            timetable: timetableData[classId],
            periodsPerDay,
            periodConfig,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          })
        )
      );
      setSuccessMessage("Timetables saved successfully");
    } catch (error) {
      setError("Failed to save timetables");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const printTimetableByClass = () => {
    setError("");
    setSuccessMessage("");
    if (Object.keys(timetables).length === 0) {
      setError("No timetables generated yet");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape'
    });

    Object.keys(timetables).forEach((classId, index) => {
      if (index > 0) doc.addPage();

      const logoX = 14;
      const logoY = 10;
      const logoWidth = 20;
      const logoHeight = 20;

      doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${SCHOOL_NAME}`.toUpperCase(), logoX + logoWidth + 5, logoY + 9);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${SCHOOL_ADDRESS}. ${SCHOOL_PHONE}`, logoX + logoWidth + 5, logoY + 15);

      const className = classes.find(c => c.id === classId)?.name || 'Unknown Class';
      doc.setFontSize(18);
      doc.setFont('helvetica', 'normal');
      doc.text(`Timetable for ${className}`, logoX + logoWidth + 5, logoY + 24);

      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      const maxPeriods = Math.max(...Object.values(periodsPerDay));
      const head = ['Day', ...periodConfig.slice(0, maxPeriods).map(p => p.time)];
      const tableData = days.map(day => {
        const row = [day];
        timetables[classId][day].forEach((slot, periodIndex) => {
          if (periodIndex >= periodsPerDay[day]) {
            row.push('N/A');
          } else if (periodConfig[periodIndex].isBreak) {
            row.push('Break');
          } else if (slot === '-' || !slot) {
            row.push('Free');
          } else if (typeof slot === 'object') {
            const teacher = allTeachers.find(t => t.id === slot.teacherId);
            const subject = subjects.find(s => s.id === slot.subjectId);
            row.push(`${subject?.name || '?'}\n${teacher ? `(${teacher.firstName})` : '?'}`);
          } else {
            row.push(slot);
          }
        });
        return row;
      });

      autoTable(doc, {
        startY: logoY + 38,
        head: [head],
        body: tableData,
        styles: {
          fontSize: 10,
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          minCellHeight: 20,
          valign: "middle",
          halign: "center",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: false
        },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
        columnStyles: {
          0: { cellWidth: 24, fontStyle: "bold" }
        }
      });
    });

    doc.save('timetables_by_class.pdf');
  };

  const printTimetableByTeacher = () => {    
    setError("");
    setSuccessMessage("");
    if (Object.keys(timetables).length === 0) {
      setError("No timetables generated yet");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF({
      orientation: 'landscape'
    });

    const teacherSchedules = {};
    const maxPeriods = Math.max(...Object.values(periodsPerDay));

    allTeachers.forEach(teacher => {
      if (selectedTeachers.includes(teacher.id)) {
        teacherSchedules[teacher.id] = {};
        ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].forEach(day => {
          teacherSchedules[teacher.id][day] = Array(maxPeriods).fill('Free');
        });
      }
    });

    Object.keys(timetables).forEach(classId => {
      const classesName = classes.find(c => c.id === classId)?.name || 'Unknown Class';
      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      
      days.forEach(day => {
        // Defensive check: Ensure timetables[classId][day] exists and is an array
        if (timetables[classId] && Array.isArray(timetables[classId][day])) {
          timetables[classId][day].forEach((slot, periodIndex) => {
            if (periodIndex < periodsPerDay[day] && slot && typeof slot === 'object' && slot.teacherId) {
              if (!teacherSchedules[slot.teacherId]) {
                teacherSchedules[slot.teacherId] = {};
                days.forEach(d => {
                  teacherSchedules[slot.teacherId][d] = Array(maxPeriods).fill('Free');
                });
              }
              const subject = subjects.find(s => s.id === slot.subjectId);
              teacherSchedules[slot.teacherId][day][periodIndex] = 
                periodConfig[periodIndex].isBreak ? 'Break' :
                `${subject?.name || '?'}\n(${classesName})`;
            }
          });
        } else {
          console.warn(`Invalid or missing timetable data for classId: ${classId}, day: ${day}`);
        }
      });
    });

    Object.keys(teacherSchedules).forEach((teacherId, index) => {
      if (index > 0) doc.addPage();

      const logoX = 14;
      const logoY = 10;
      const logoWidth = 20;
      const logoHeight = 20;

      doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text(`${SCHOOL_NAME}`.toUpperCase(), logoX + logoWidth + 5, logoY + 9);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.text(`${SCHOOL_ADDRESS}. ${SCHOOL_PHONE}`, logoX + logoWidth + 5, logoY + 15);
      
      const teacher = allTeachers.find(t => t.id === teacherId);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'normal');
      doc.text(`Timetable for ${teacher?.firstName} ${teacher?.lastName}`, logoX + logoWidth + 5, logoY + 24);
      
      const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      const head = ['Day', ...periodConfig.slice(0, maxPeriods).map(p => p.time)];
      const tableData = days.map(day => {
        const row = [day];
        const periodsToShow = periodsPerDay[day];
        for (let i = 0; i < maxPeriods; i++) {
          if (i < periodsToShow) {
            row.push(periodConfig[i].isBreak ? 'Break' : teacherSchedules[teacherId][day][i]);
          } else {
            row.push('N/A');
          }
        }
        return row;
      });

      autoTable(doc, {
        startY: logoY + 38,
        head: [head],
        body: tableData,
        styles: {
          fontSize: 10,
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          minCellHeight: 20,
          valign: "middle",
          halign: "center",
          textColor: [0, 0, 0],
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          halign: "center",
        },
        alternateRowStyles: {
          fillColor: false
        },
        tableLineColor: [0, 0, 0],
        tableLineWidth: 0.1,
        columnStyles: {
          0: { cellWidth: 24, fontStyle: "bold" }
        }
      });
    });

    doc.save('timetables_by_teacher.pdf');
  };

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  if (!userData) {
    return <Loader text={"READING"} />;
  }

  const allowedRoles = ['Admin', 'Cashier'];

  return (
    <>
      {update && <Loader text={"PROCESSING"} />}
      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <div className="column align-center justify-center">
              <img src={Logo} className="middle-logo" alt="logo" />
            </div>
            <Link to="#" className="home-link" onClick={fetchData}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items column align-center scroll-fixed">
            <h2 className="manage-page-heading caps size4">Manage Timetable</h2>
            <div className="row align-center gap-1" style={{marginBottom: '10px', width: '100%'}}>
              <button
                className="login-button"
                style={{padding: '5px', fontSize: '0.8rem'}}
                onClick={handleSelectTeachers}
              >
                Select Teachers
              </button>
              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem'}} 
                onClick={() => setIsAssignModalOpen(true)} 
                disabled={selectedTeachers.length === 0}
              >
                Assign Subject
              </button>
              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem'}} 
                onClick={() => setIsSetPeriodsModalOpen(true)}
              >
                Periods per Day
              </button>
            </div>
            <div className="row align-center gap-1" style={{width: '100%'}}>
              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem', marginTop: '0px'}} 
                onClick={generateTimetables} 
                disabled={selectedTeachers.length === 0}
              >
                Generate tables
              </button>
              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem', marginTop: '0px'}}
                onClick={printTimetableByClass}
                disabled={Object.keys(timetables).length === 0}
              >
                Print per Class
              </button>
              <button 
                className="login-button" 
                style={{padding: '5px', fontSize: '0.8rem', marginTop: '0px'}}
                onClick={printTimetableByTeacher}
                disabled={Object.keys(timetables).length === 0}
              >
                Print per Teacher
              </button>
            </div>

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list scrollable">
                {isFetchingData ? (
                  <p>Loading data ...</p>
                ) : selectedTeachers.length === 0 ? (
                  <p>No teachers selected</p>
                ) : (
                  selectedTeachers.map((teacherId) => {
                    const teacher = allTeachers.find((t) => t.id === teacherId);
                    return (
                      <div key={teacherId} className="class-item">
                        <p>
                          <strong>{`${teacher.firstName} ${teacher.lastName}`}</strong>
                        </p>
                        {teacherAssignments[teacherId] && teacherAssignments[teacherId].length > 0 ? (
                          <ul>
                            {teacherAssignments[teacherId].map((assignment, index) => (
                              <li key={index}>
                                {subjects.find((s) => s.id === assignment.subjectId)?.name}<br />{" "}
                                {classes.find((c) => c.id === assignment.classId)?.name} (
                                {assignment.periods} p)
                                <br />
                                <div className="row align-center justify-center gap-15" style={{marginTop: '5px'}}>
                                  <button
                                    className="list-btn"
                                    onClick={() => handleEditAssignment(teacherId, index)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="list-btn"
                                    onClick={() => removeAssignment(teacherId, index)}
                                  >
                                    <i className="fas fa-trash-alt"></i>
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p>No assignments yet</p>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
              {Object.keys(timetables).length > 0 && (
                <div>
                  <h3>Generated Timetables</h3>
                  {Object.keys(timetables).map((classId) => (
                    <div key={classId}>
                      <h4>{classes.find((c) => c.id === classId)?.name}</h4>                  
                      <div style={{ 
                        width: "100%", 
                        overflowX: "auto",
                        marginBottom: "20px"
                      }}>
                        <table style={{ 
                          tableLayout: "fixed", 
                          borderCollapse: "collapse",
                          minWidth: "100%"
                        }}>
                          <thead>
                            <tr>
                              <th style={{ 
                                width: "80px",
                                minWidth: "80px",
                                border: "1px solid #ddd", 
                                textAlign: "left",
                                position: "sticky",
                                left: 0,
                              }}>
                                Day/Time
                              </th>
                              {periodConfig.slice(0, Math.max(...Object.values(periodsPerDay))).map((period, i) => (
                                <th
                                  key={i} 
                                  style={{
                                    border: "1px solid #ddd", 
                                    textAlign: "center"
                                  }}
                                >
                                  Period {i + 1}<br/><span className="size5">{period.time}</span>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => (
                              <tr key={day} style={{fontSize: '0.9rem',}}>
                                <td 
                                  style={{ 
                                    width: "60px",
                                    minWidth: "60px",
                                    border: "1px solid #ddd", 
                                    textAlign: "left",
                                    position: "sticky",
                                    fontSize: '0.8rem',
                                    left: 0,
                                  }}
                                >
                                  {day}
                                </td>
                                {timetables[classId][day].map((slot, index) => (
                                  <td
                                    key={index}
                                    style={{
                                      width: "30px",
                                      minWidth: "30px",
                                      border: "1px solid #ddd",
                                      textAlign: "center",
                                      cursor: periodConfig[index]?.isBreak ? "default" : "pointer",
                                      backgroundColor: periodConfig[index]?.isBreak ? "#f2f2f2" : "transparent"
                                    }}
                                    onClick={() => handleEditCell(classId, day, index)}
                                  >
                                    {slot && typeof slot === "object" && slot.subjectId
                                      ? subjects.find((s) => s.id === slot.subjectId)?.code || "-"
                                      : slot || "-"}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isSelectTeachersModalOpen}
        onRequestClose={() => setIsSelectTeachersModalOpen(false)}
        contentLabel="Select Teachers"
      >
        <h2>Select Teachers</h2>
        <div className="session-form">
          {allTeachers.map((teacher) => (
            <div key={teacher.id}>
              <input
                type="checkbox"
                className="gold-tick"
                id={teacher.id}
                checked={selectedTeachers.includes(teacher.id)}
                onChange={(e) => {
                  setSelectedTeachers((prev) =>
                    e.target.checked
                      ? [...prev, teacher.id]
                      : prev.filter((id) => id !== teacher.id)
                  );
                }}
              />
              <label htmlFor={teacher.id}>{`${teacher.firstName} ${teacher.lastName}`}</label>
            </div>
          ))}
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => setIsSelectTeachersModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={confirmSelectedTeachers}
            >
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isAssignModalOpen}
        onRequestClose={() => setIsAssignModalOpen(false)}
        contentLabel="Assign Subject"
      >
        <h2>Assign Subject</h2>
        <form className="session-form">
          <select
            className="input-field"
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
          >
            <option value="">Select Teacher</option>
            {selectedTeachers.map((teacherId) => {
              const teacher = allTeachers.find((t) => t.id === teacherId);
              return (
                <option key={teacherId} value={teacherId}>
                  {`${teacher.firstName} ${teacher.lastName}`}
                </option>
              );
            })}
          </select>
          <select
            className="input-field"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((classItem) => (
              <option key={classItem.id} value={classItem.id}>
                {classItem.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedPeriods}
            onChange={(e) => setSelectedPeriods(Number(e.target.value))}
          >
            {Array.from({ length: 6 }, (_, i) => i + 1).map((num) => (
              <option key={num} value={num}>
                {num} Period{num > 1 ? "s" : ""}
              </option>
            ))}
          </select>
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => setIsAssignModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={handleAssignSubject}
            >
              Assign
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditAssignmentModalOpen}
        onRequestClose={() => setIsEditAssignmentModalOpen(false)}
        contentLabel="Edit Assignment"
      >
        <h2>Edit Assignment</h2>
        <form className="session-form">
          <select
            className="input-field"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">Select Class</option>
            {classes.map((classItem) => (
              <option key={classItem.id} value={classItem.id}>
                {classItem.name}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedPeriods}
            onChange={(e) => setSelectedPeriods(Number(e.target.value))}
          >
            {Array.from({ length: 6 }, (_, i) => i + 1).map((num) => (
              <option key={num} value={num}>
                {num} Period{num > 1 ? "s" : ""}
              </option>
            ))}
          </select>
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsEditAssignmentModalOpen(false)}>
              Cancel
            </button>
            <button className="login-button" type="button" onClick={saveAssignmentEdit}>
              Save
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isSetPeriodsModalOpen}
        onRequestClose={() => setIsSetPeriodsModalOpen(false)}
        contentLabel="Set Periods per Day"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div style={{ maxHeight: '500px', overflowY: 'auto', marginBottom: '10px' }}>
          <h2>Set Periods per Day</h2>
          <div className="session-form">
            <h3>Period Configurations</h3>
            {periodConfig.map((period, i) => (
              <div key={i} className="align-center" style={{marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <label>Period {i + 1} Time:</label>
                <input
                  type="text"
                  className="input-field"
                  value={period.time}
                  onChange={(e) => {
                    const newConfig = [...periodConfig];
                    newConfig[i] = { ...newConfig[i], time: e.target.value };
                    setPeriodConfig(newConfig);
                  }}
                />
                <label>
                  Break:
                  <input
                    type="checkbox"
                    className="gold-tick"
                    checked={period.isBreak}
                    onChange={(e) => {
                      const newConfig = [...periodConfig];
                      newConfig[i] = { ...newConfig[i], isBreak: e.target.checked };
                      setPeriodConfig(newConfig);
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
          <h3>Periods per Day</h3>
          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"].map((day) => (
            <div key={day} className="session-form">
              <label>{day}</label>
              <select
                className="input-field"
                value={periodsPerDay[day]}
                onChange={(e) => setPeriodsForDay(day, Number(e.target.value))}
              >
                {Array.from({ length: 10 }, (_, i) => i + 3).map((num) => (
                  <option key={num} value={num}>
                    {num} Periods
                  </option>
                ))}
              </select>
            </div>
          ))}
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => setIsSetPeriodsModalOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEditCellModalOpen}
        onRequestClose={() => setIsEditCellModalOpen(false)}
        contentLabel="Edit Timetable Cell"
      >
        <h2>Edit Timetable Cell</h2>
        <form className="session-form">
          <select
            className="input-field"
            value={selectedTeacher}
            onChange={(e) => setSelectedTeacher(e.target.value)}
          >
            <option value="">Select Teacher</option>
            {allTeachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {`${teacher.firstName} ${teacher.lastName}`}
              </option>
            ))}
          </select>
          <select
            className="input-field"
            value={selectedSubject}
            onChange={(e) => setSelectedSubject(e.target.value)}
          >
            <option value="">Select Subject</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => setIsEditCellModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={saveCellEdit}
            >
              Save
            </button>            
          </div>
          <button
              className="login-button"
              type="button"
              onClick={removeCellSubject}
          >
            Remove Subject
          </button>
        </form>
      </Modal>
    </>
  );
};

export default Timetable;